import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssessmentContext } from 'services/context/assessmentContext/useAssessmentContext';
import { useVideoRecordingContext } from 'services/context/videoRecordingContext/useVideoRecordingContext';
import { setErrorPage } from 'services/requests/helpers/setErrorPage';
import { microphone } from 'components/global/assets/microphone';
import { VideoDurationTimer } from 'components/global/videoUpload/videoDurationTimer';
import RecordRTC from 'recordrtc';
import Button from 'components/global/Buttons/Button';
import { VIDEO_TIME_LIMIT } from 'utils/constants/constants.js';
import {
  useAudioAnalyser,
  useMediaStream
} from 'components/global/videoUpload/hooksVideoUpload';
import {
  buildPendingFileRecord,
  storePendingFile
} from 'services/requests/slotRequests';
import { getStoredFile, removeFile } from 'utils/indexedDBService';
import {
  Container,
  HeaderContainer,
  VideoContainer,
  PreviewVideo,
  RecordedVideo,
  ActionsContainer,
  MicrophoneContainer,
  MicrophoneMeter,
  MicrophoneLevel,
  MicrophoneIcon,
  RecordingIndicator,
  ButtonsContainer,
  StatusMessage
} from 'components/global/videoUpload/styles';
import { useUploadContext } from 'services/context/uploadContext/uploadContext';
import { useStreamHealth } from './useStreamHealth';
import { TroubleshootingVideoRecord } from 'components/global/videoUpload/TroubleshootingVideoRecord';

export const VideoUploadInput = ({ label, formikProps }) => {
  const { t } = useTranslation();
  const {
    currentQuestionNumber,
    state: { currentSlotId, assessmentAttempt },
    dispatch
  } = useAssessmentContext();
  const { dispatch: uploadDispatch } = useUploadContext();
  const { dispatch: videoRecordingDispatch } = useVideoRecordingContext();
  const { stream, error, loading, initializeMedia } = useMediaStream();
  const { statusMessage, isNotAbleToRecord } = useStreamHealth({
    stream,
    loading,
    initializeMedia,
    error
  });
  const micLevel = useAudioAnalyser(stream);
  const [recording, setRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const previewRef = useRef(null);
  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const timerRef = useRef(null);
  const fileName = currentSlotId;

  const showErrorPage = useCallback(() => {
    setErrorPage(
      {
        data: {
          message: `${t('troubleshootingPanel.compatibilityIssue')} ${t(
            'troubleshootingPanel.updateBrowserOrDevice'
          )}`
        },
        status: 400
      },
      dispatch
    );
  }, [dispatch, t]);

  useEffect(() => {
    if (stream && previewRef.current) {
      try {
        previewRef.current.srcObject = stream;
      } catch (error) {
        console.error(
          'Video Recording Error: Failed to set stream to preview video element:',
          error
        );
      }
    }
  }, [stream]);

  const startRecording = () => {
    if (!stream) {
      console.error(
        'Video Recording Error: Cannot start recording - no media stream available'
      );
      showErrorPage();
      return;
    }

    try {
      recorderRef.current = new RecordRTC(stream, {
        type: 'video',
        mimeType: 'video/webm;codecs=vp8,opus',
        canvas: {
          width: 640,
          height: 480
        },
        frameInterval: 15,
        frameRate: 15,
        numberOfAudioChannels: 1,
        bitsPerSecond: 200000,
        videoConstraints: {
          width: { max: 640 },
          height: { max: 480 },
          frameRate: 15,
          facingMode: 'user',
          bitrate: 200000
        }
      });

      recorderRef.current.startRecording();
      setRecording(true);
      setRecordingTime(0);

      if (videoRef.current) {
        try {
          videoRef.current.srcObject = stream;
        } catch (error) {
          console.error(
            'Video Recording Error: Failed to set stream to video element during recording start:',
            error
          );
        }
      }
    } catch (error) {
      console.error('Video Recording Error: Failed to start recording:', error);
      setRecording(false);
      showErrorPage();
    }
  };

  const stopRecording = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!recorderRef.current) {
        console.warn(
          'Video Recording Warning: No active recorder found when trying to stop recording'
        );
        resolve();
        return;
      }

      recorderRef.current.stopRecording(async () => {
        try {
          const blob = recorderRef.current.getBlob();

          if (!blob || blob.size === 0) {
            console.error(
              'Video Recording Error: Recording produced empty or invalid blob'
            );
            showErrorPage();
            reject(new Error('Recording produced empty blob'));
            return;
          }

          setVideoBlob(blob);
          setRecording(false);
          if (videoRef.current) {
            videoRef.current.srcObject = null;
            videoRef.current.src = URL.createObjectURL(blob);

            let file = new File([blob], `${fileName}.webm`, {
              type: 'video/webm'
            });
            // Add a custom property to identify this as a recorded video
            file.isRecordedVideo = true;

            const attemptUUid = assessmentAttempt.uuid;
            const assessmentSlug = assessmentAttempt.assessment.slug;

            // Arm the in-memory fallback before SET_FILE enables the next
            // button. Submitting during the IndexedDB write below would
            // otherwise find neither store and drop the recording silently.
            videoRecordingDispatch({
              type: 'SET_IN_MEMORY_VIDEO_RECORD',
              payload: {
                inMemoryVideoRecord: buildPendingFileRecord(
                  attemptUUid,
                  currentSlotId,
                  assessmentSlug,
                  currentQuestionNumber,
                  file
                )
              }
            });

            formikProps.setFieldValue(fileName, file, true);
            dispatch({
              type: 'SET_FILE',
              payload: {}
            });
            dispatch({
              type: 'SET_FILE',
              payload: { [fileName]: file }
            });
            // Store the file in IndexedDB
            await storePendingFile(
              attemptUUid,
              currentSlotId,
              assessmentSlug,
              currentQuestionNumber,
              file,
              uploadDispatch,
              dispatch
            );
          }

          // Clear recording state from context
          videoRecordingDispatch({
            type: 'CLEAR_RECORDING_STATE'
          });

          resolve();
        } catch (error) {
          console.error('Error in stopRecording:', error);
          showErrorPage();
          reject(error);
        }
      });
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    });
  }, [
    fileName,
    formikProps,
    dispatch,
    assessmentAttempt.uuid,
    assessmentAttempt.assessment.slug,
    currentSlotId,
    currentQuestionNumber,
    uploadDispatch,
    videoRecordingDispatch,
    showErrorPage
  ]);

  // Update context when recording state changes
  // video recording context is used to manage recording state across the application
  // this is useful for stopping the recording when the section or slot times out
  useEffect(() => {
    if (recording) {
      videoRecordingDispatch({
        type: 'SET_RECORDING_STATE',
        payload: {
          isRecording: true,
          stopRecording: stopRecording,
          currentSlotId: currentSlotId
        }
      });
    } else {
      videoRecordingDispatch({
        type: 'CLEAR_RECORDING_STATE'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, currentSlotId, videoRecordingDispatch]);

  // Check for existing file in IDB and load it
  const loadExistingFile = useCallback(async () => {
    try {
      const record = await getStoredFile(assessmentAttempt.uuid, currentSlotId);
      const blob = record?.blob;
      if (blob && videoRef.current) {
        try {
          const url = URL.createObjectURL(blob);
          videoRef.current.src = url;
          setVideoBlob(blob);
          dispatch({
            type: 'SET_FILE',
            payload: { [fileName]: blob }
          });
          formikProps.setFieldValue(fileName, blob, true);
        } catch (error) {
          console.error(
            'Video Recording Error: Failed to load existing video file:',
            error
          );
        }
      }
    } catch (error) {
      console.error(
        'Video Recording Error: Failed to retrieve existing file from IndexedDB:',
        error
      );
    }
  }, [
    videoRef,
    assessmentAttempt,
    currentSlotId,
    setVideoBlob,
    dispatch,
    fileName,
    formikProps
  ]);

  useEffect(() => {
    if (!videoBlob) {
      loadExistingFile();
    }
  }, [videoBlob, loadExistingFile]);

  const startDeletion = () => {
    setIsDeleting(true);
  };

  const cancelDelete = () => {
    setIsDeleting(false);
  };

  const deleteVideo = () => {
    try {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = '';
      }
      setVideoBlob(null);
      setIsDeleting(false);
      setRecordingTime(0);

      // Set field value to null, for some reason, this doesn't work as expected
      formikProps.resetForm({
        ...formikProps.values,
        [fileName]: null
      });
      formikProps.setFieldValue(fileName, null, true);

      dispatch({
        type: 'SET_FILE',
        payload: {}
      });

      removeFile(assessmentAttempt.uuid, currentSlotId, uploadDispatch);
      // Drop the fallback too, otherwise the deleted take is still uploaded if
      // the slot is submitted before a new recording is made.
      videoRecordingDispatch({
        type: 'SET_IN_MEMORY_VIDEO_RECORD',
        payload: { inMemoryVideoRecord: null }
      });
    } catch (error) {
      console.error('Video Recording Error: Failed to delete video:', error);
    }
  };

  useEffect(() => {
    if (recording && recordingTime < VIDEO_TIME_LIMIT) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prevTime => {
          if (prevTime + 1 >= VIDEO_TIME_LIMIT) {
            stopRecording().catch(error => {
              console.error(
                'Video Recording Error: Failed to stop recording at time limit:',
                error
              );
            });
            return VIDEO_TIME_LIMIT;
          }
          return prevTime + 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [recording, recordingTime, stopRecording]);

  const remainingTime = VIDEO_TIME_LIMIT - recordingTime;

  return (
    <Container>
      {label && <h2>{label}</h2>}

      {/* Actions */}
      <HeaderContainer>
        <VideoDurationTimer timer={remainingTime} />
      </HeaderContainer>
      <VideoContainer>
        {/* Videos */}
        <div>
          {/* When we are not recording or have a video blob show the preview*/}
          {recording && (
            <RecordingIndicator>
              <svg height="100" width="100">
                <circle cx="50" cy="50" r="10" fill="red" />
              </svg>
            </RecordingIndicator>
          )}

          <PreviewVideo
            ref={previewRef}
            width="640"
            height="480"
            autoPlay
            muted
            playsInline
            recording={recording}
            videoBlob={videoBlob}
          />
          <RecordedVideo
            ref={videoRef}
            width="640"
            height="480"
            autoPlay
            muted={recording}
            playsInline
            controls={!!videoBlob}
            recording={recording}
            videoBlob={videoBlob}
          />
        </div>
        {/* Microphone indicator*/}
        <MicrophoneContainer videoBlob={videoBlob}>
          <MicrophoneMeter>
            <MicrophoneLevel micLevel={micLevel} />
          </MicrophoneMeter>
          <MicrophoneIcon>{microphone}</MicrophoneIcon>
        </MicrophoneContainer>
      </VideoContainer>

      {/* Status Message */}
      <StatusMessage status={statusMessage?.status}>
        {statusMessage?.message}
      </StatusMessage>
      <ActionsContainer>
        {!recording && !videoBlob && (
          <Button
            text={t('videoQuestion.startRecording')}
            onClick={startRecording}
            disabled={isNotAbleToRecord}
          />
        )}
        {recording && (
          <Button
            text={t('videoQuestion.stopRecording')}
            onClick={stopRecording}
          />
        )}
        {videoBlob && !isDeleting && (
          <Button
            text={t('videoQuestion.retakeRecording')}
            onClick={startDeletion}
            className="warning"
          />
        )}
        {videoBlob && isDeleting && (
          <>
            <h3>{t('videoQuestion.confirmRetake')}</h3>
            <ButtonsContainer>
              <Button
                text={t('videoQuestion.noPrevious')}
                onClick={cancelDelete}
              />
              <Button
                text={t('videoQuestion.yesRetake')}
                onClick={deleteVideo}
                className="warning"
              />
            </ButtonsContainer>
          </>
        )}
      </ActionsContainer>
      <TroubleshootingVideoRecord />
    </Container>
  );
};
